import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { getLoNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { api, supabase } from '../../lib/supabase';
import {
  ActivityType,
  CustomTask,
  InputConfig,
  ProgressWithStep,
  StepCategoryRecord,
  StepStatus,
  activityTypeCopy,
  priorityColors,
  statusColors,
  statusLabels,
} from '../../types';
import { ExclamationTriangleIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const activityAccent: Record<ActivityType, string> = {
  task: 'bg-[#eef4ff] text-lumin-primary',
  form: 'bg-[#e0f2ff] text-[#016185]',
  document_upload: 'bg-[#e8fdf4] text-emerald-600',
  training: 'bg-[#f4e8ff] text-purple-600',
  meeting: 'bg-[#e0f0ff] text-sky-700',
  call: 'bg-[#fff2e1] text-amber-600',
  payment: 'bg-[#fff4e5] text-amber-700',
  signature: 'bg-[#ffe9f0] text-pink-600',
  video: 'bg-[#e7ecff] text-indigo-600',
  custom: 'bg-[#f2f4f7] text-slate-500',
};

// Helper to replace template variables in notes/URLs
const replaceTemplateVars = (text: string | null | undefined, locationId: string | null | undefined): string => {
  if (!text) return '';
  return text.replace(/\{\{location_id\}\}/g, locationId || '');
};

// Phone Input Form Component
const PhoneInputForm: React.FC<{
  config: InputConfig;
  onSubmit: (phone: string) => Promise<void>;
  loading: boolean;
}> = ({ config, onSubmit, loading }) => {
  const [phone, setPhone] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(phone);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4 p-4 bg-slate-50 rounded-2xl">
      <h4 className="text-sm font-semibold text-slate-700">{config.label || 'Phone Number'}</h4>
      <input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder={config.placeholder || 'Enter phone number'}
        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lumin-primary"
        required={config.required !== false}
      />
      <button
        type="submit"
        disabled={loading || !phone}
        className="w-full px-4 py-3 rounded-xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68] disabled:opacity-50"
      >
        {loading ? 'Saving...' : 'Save & Complete'}
      </button>
    </form>
  );
};

// File Upload Form Component (for W9, Banking PDFs, etc.)
const FileUploadForm: React.FC<{
  config: InputConfig;
  onSubmit: (file: File) => Promise<void>;
  loading: boolean;
}> = ({ config, onSubmit, loading }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (file) {
      await onSubmit(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4 p-4 bg-slate-50 rounded-2xl">
      <h4 className="text-sm font-semibold text-slate-700">{config.label || 'Upload Document'}</h4>

      {/* Download blank form link */}
      {config.downloadUrl && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-700 mb-2">Step 1: Download and fill out the form</p>
          <a
            href={config.downloadUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Download Blank Form
          </a>
        </div>
      )}

      {/* File upload area */}
      <div
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          dragActive ? 'border-lumin-primary bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <p className="text-sm text-slate-600 mb-2">
          {config.downloadUrl ? 'Step 2: Upload your file' : 'Select or drop your file below'}
        </p>
        <input
          type="file"
          accept={config.accept || '.pdf'}
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="inline-block px-4 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-medium cursor-pointer hover:bg-slate-300"
        >
          Choose File
        </label>
        <p className="text-xs text-slate-400 mt-2">or drag and drop here</p>

        {file && (
          <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-sm text-emerald-700 font-medium">{file.name}</p>
            <p className="text-xs text-emerald-600">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={loading || !file}
        className="w-full px-4 py-3 rounded-xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68] disabled:opacity-50"
      >
        {loading ? 'Uploading...' : 'Upload & Complete'}
      </button>
    </form>
  );
};

// Resource Download Component
const ResourceDownload: React.FC<{
  resourceType: string;
  resourceUrl?: string | null;
}> = ({ resourceType, resourceUrl }) => {
  if (!resourceUrl) {
    return (
      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
        <p className="text-sm text-amber-700">
          Your {resourceType.replace('_', ' ')} is not yet available. Please check back later or contact your admin.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
      <p className="text-sm text-emerald-700 mb-3">
        Your {resourceType.replace('_', ' ')} is ready to download!
      </p>
      <a
        href={resourceUrl}
        download
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
        Download {resourceType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </a>
    </div>
  );
};

interface LoanOfficerWorkspaceProps {
  section?: string; // 'onboarding' | 'hands_on'
}

const LoanOfficerWorkspace: React.FC<LoanOfficerWorkspaceProps> = ({ section = 'onboarding' }) => {
  const navigate = useNavigate();
  const { loanOfficer, isImpersonating, impersonatedLO, realUser, stopImpersonation } = useAuth();
  const [progress, setProgress] = useState<ProgressWithStep[]>([]);
  const [tasks, setTasks] = useState<CustomTask[]>([]);
  const [categories, setCategories] = useState<StepCategoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStepId, setUpdatingStepId] = useState<string | null>(null);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [selectedStep, setSelectedStep] = useState<ProgressWithStep | null>(null);
  const [selectedTask, setSelectedTask] = useState<CustomTask | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const loadData = async () => {
    if (!loanOfficer) return;
    try {
      setLoading(true);
      const [progressData, taskData, categoryData] = await Promise.all([
        api.getLOProgress(loanOfficer.id),
        api.getCustomTasks(loanOfficer.id),
        api.getStepCategories(),
      ]);
      setProgress(progressData as ProgressWithStep[]);
      setTasks(taskData.filter((task) => task.status !== 'completed'));
      setCategories(categoryData);
    } catch (error) {
      // Error handled silently
      alert('Failed to load onboarding data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    if (!loanOfficer?.id) return;

    const channel = supabase
      .channel(`lo-progress-${loanOfficer.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loan_officer_progress',
          filter: `loan_officer_id=eq.${loanOfficer.id}`,
        },
        () => loadData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanOfficer?.id]);

  const groupedSteps = useMemo(() => {
    const map = new Map<string, ProgressWithStep[]>();
    progress
      .filter((item) => item.step.section === section)
      .filter((item) => item.step.target_audience === 'loan_officer')
      .filter((item) => {
        // If step has no required flags, show it to everyone
        if (!item.step.required_flags || item.step.required_flags.length === 0) {
          return true;
        }
        // Check if LO has all required flags
        return item.step.required_flags.every(
          (flag) => loanOfficer?.profile_flags?.[flag] === true
        );
      })
      .forEach((item) => {
        if (!map.has(item.step.category)) {
          map.set(item.step.category, []);
        }
        map.get(item.step.category)!.push(item);
      });

    const sorted = categories.map((category) => ({
      category,
      steps: (map.get(category.slug) || []).sort(
        (a, b) => a.step.sequential_order - b.step.sequential_order
      ),
    }));

    return sorted;
  }, [categories, progress, loanOfficer, section]);

  // Calculate LO-only summary (excludes admin/VA tasks)
  const loOnlySummary = useMemo(() => {
    // Get all LO-only steps from groupedSteps
    const allLoSteps = groupedSteps.flatMap(({ steps }) => steps);
    const total = allLoSteps.length;
    if (total === 0) return null;

    const completed = allLoSteps.filter((s) => s.status === 'completed').length;
    const inProgress = allLoSteps.filter((s) => s.status === 'in_progress').length;

    return {
      total_steps: total,
      completed_steps: completed,
      in_progress_steps: inProgress,
      completion_percentage: (completed / total) * 100,
    };
  }, [groupedSteps]);

  // Collapse all categories by default (only on initial load)
  const [initialCollapseSet, setInitialCollapseSet] = useState(false);
  useEffect(() => {
    if (!initialCollapseSet && groupedSteps.length > 0) {
      const newCollapsed = new Set<string>();
      groupedSteps.forEach(({ category }) => {
        newCollapsed.add(category.id);
      });
      setCollapsedCategories(newCollapsed);
      setInitialCollapseSet(true);
    }
  }, [groupedSteps, initialCollapseSet]);

  // Keep selectedStep in sync with progress updates (fixes stale modal state)
  useEffect(() => {
    if (selectedStep) {
      const updated = progress.find(p => p.id === selectedStep.id);
      if (updated && updated.status !== selectedStep.status) {
        setSelectedStep(updated);
      }
    }
  }, [progress, selectedStep]);

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Check if all dependencies for a step are completed
  const areDependenciesMet = (item: ProgressWithStep): boolean => {
    const deps = item.step.dependencies;
    if (!deps || deps.length === 0) return true;

    // Check if all dependency step_ids have completed status in progress
    return deps.every((depStepId) => {
      const depProgress = progress.find((p) => p.step_id === depStepId);
      return depProgress?.status === 'completed';
    });
  };

  // Get the names of incomplete dependencies for display
  const getIncompleteDependencies = (item: ProgressWithStep): string[] => {
    const deps = item.step.dependencies;
    if (!deps || deps.length === 0) return [];

    return deps
      .filter((depStepId) => {
        const depProgress = progress.find((p) => p.step_id === depStepId);
        return depProgress?.status !== 'completed';
      })
      .map((depStepId) => {
        const depProgress = progress.find((p) => p.step_id === depStepId);
        return depProgress?.step?.task_name || 'Unknown task';
      });
  };

  const nextStep = useMemo(() => {
    return (
      [...progress]
        .filter((item) => item.step.section === section)
        .filter((item) => item.step.target_audience === 'loan_officer')
        .filter((item) => {
          // If step has no required flags, show it to everyone
          if (!item.step.required_flags || item.step.required_flags.length === 0) {
            return true;
          }
          // Check if LO has all required flags
          return item.step.required_flags.every(
            (flag) => loanOfficer?.profile_flags?.[flag] === true
          );
        })
        .filter((item) => item.status !== 'completed')
        .sort((a, b) => a.step.sequential_order - b.step.sequential_order)[0] || null
    );
  }, [progress, loanOfficer, section]);

  const handleStatusUpdate = async (progressId: string, status: StepStatus) => {
    const previous = progress;
    setProgress((prev) => prev.map((item) => (item.id === progressId ? { ...item, status } : item)));

    try {
      setUpdatingStepId(progressId);
      await api.updateProgressStatus(progressId, status);
      await loadData();
    } catch (error) {
      // Error handled silently
      alert('Unable to update status.');
      setProgress(previous);
    } finally {
      setUpdatingStepId(null);
    }
  };

  const markTaskComplete = async (taskId: string) => {
    try {
      await api.updateCustomTask(taskId, { status: 'completed', completed_date: new Date().toISOString() });
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
      setSelectedTask(null); // Close modal if open
    } catch (error) {
      // Error handled silently
      alert('Unable to complete task.');
    }
  };

  // Form submission handlers for input_config
  const handlePhoneSubmit = async (phone: string) => {
    if (!loanOfficer || !selectedStep) return;
    const stepId = selectedStep.id; // Capture ID before closing modal
    try {
      setFormSubmitting(true);
      await api.updateLOPhone(loanOfficer.id, phone);
      setSelectedStep(null); // Close modal first to prevent loop
      await handleStatusUpdate(stepId, 'completed');
    } catch (error) {
      // Error handled silently
      alert('Failed to save phone number.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // File upload handler for W9, Banking PDFs, etc.
  const handleFileUpload = async (file: File) => {
    if (!loanOfficer || !selectedStep) return;
    const stepId = selectedStep.id; // Capture ID before closing modal
    const docType = selectedStep.step.input_config?.documentType || 'documents';
    try {
      setFormSubmitting(true);

      let storagePath: string;

      if (docType === 'w9') {
        // Use private w9-forms bucket for W9 documents
        storagePath = await api.uploadW9Document(loanOfficer.id, file);

        // Update w9_submissions table with storage path
        await api.submitW9({
          loan_officer_id: loanOfficer.id,
          name: `${loanOfficer.first_name} ${loanOfficer.last_name}`,
          tax_classification: 'pending_review',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          ssn_or_ein: '',
          pdf_url: storagePath, // Store path, not public URL
        });
      } else {
        // Use onboarding-documents bucket for other files
        // Path structure: {userId}/{docType}/{filename} - matches RLS policy
        const fileExt = file.name.split('.').pop();
        const fileName = `${docType}-${Date.now()}.${fileExt}`;
        const filePath = `${loanOfficer.id}/${docType}/${fileName}`;

        storagePath = await api.uploadOnboardingDocument(loanOfficer.id, file, filePath);
      }

      setSelectedStep(null); // Close modal first to prevent loop
      await handleStatusUpdate(stepId, 'completed');
    } catch (error) {
      // Error handled silently
      alert('Failed to upload document.');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Helper to get resource URL based on type
  const getResourceUrl = (resourceType: string): string | null => {
    if (!loanOfficer) return null;
    if (resourceType === 'email_signature') {
      return loanOfficer.email_signature_url || null;
    }
    if (resourceType === 'headshot') {
      return loanOfficer.headshot || null;
    }
    return null;
  };

  // Check if step has input config that requires form
  const hasInputForm = (config: InputConfig | null): boolean => {
    if (!config) return false;
    return ['w9', 'banking', 'phone_input', 'text_input', 'file_upload'].includes(config.type || '');
  };

  const navigation = useMemo(() => getLoNavigation(loanOfficer?.location_id ?? undefined), [loanOfficer?.location_id]);

  const pageTitle = section === 'hands_on' ? 'Hands On Training' : 'Onboarding Portal';
  const pageDescription = section === 'hands_on'
    ? 'Live training checklist for your in-person session.'
    : 'Every requirement, resource, and signal for your onboarding flight path.';

  if (loading) {
    return (
      <AppShell title={pageTitle} description="Loading personalized timeline..." nav={navigation}>
        <div className="flex items-center justify-center py-24 text-slate-500">
          <div className="h-12 w-12 border-2 border-[#cce8f4] border-t-lumin-primary rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={pageTitle}
      description={pageDescription}
      nav={navigation}
    >
      <div className="space-y-8">
        {/* Impersonation Banner */}
        {isImpersonating && impersonatedLO && (
          <div className="rounded-2xl bg-amber-500 px-6 py-4 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              <span className="font-semibold">
                Viewing as: {impersonatedLO.first_name} {impersonatedLO.last_name}
              </span>
              <span className="text-amber-100 text-sm">
                (Logged in as {realUser?.first_name} {realUser?.last_name})
              </span>
            </div>
            <button
              onClick={() => {
                stopImpersonation();
                navigate(`/admin/loan-officers/${impersonatedLO.id}`);
              }}
              className="px-4 py-2 rounded-xl bg-white text-amber-600 font-semibold text-sm hover:bg-amber-50 transition-colors"
            >
              Exit View
            </button>
          </div>
        )}

        {loOnlySummary && (
          <section className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-lumin-primary">Trajectory</p>
                <p className="text-5xl font-semibold text-slate-900 mt-2">
                  {loOnlySummary.completion_percentage.toFixed(0)}% complete
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  {loOnlySummary.completed_steps} of {loOnlySummary.total_steps} steps locked in.
                </p>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-100 bg-[#f4fbff] p-4 text-center">
                  <p className="text-3xl font-semibold text-lumin-primary">{loOnlySummary.completed_steps}</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Completed</p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-[#fff5ef] p-4 text-center">
                  <p className="text-3xl font-semibold text-amber-600">{loOnlySummary.in_progress_steps}</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-500">In progress</p>
                </div>
              </div>
            </div>
            {nextStep && (
              <div
                className="mt-8 rounded-3xl border border-slate-100 bg-[#f9fbff] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 cursor-pointer hover:border-slate-200 transition-all"
                onClick={() => setSelectedStep(nextStep)}
              >
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-slate-500">Next up</p>
                  <h3 className="text-2xl font-semibold text-slate-900">{nextStep.step.task_name}</h3>
                  <p className="text-sm text-slate-500 mt-2">
                    {replaceTemplateVars(nextStep.step.notes, loanOfficer?.location_id) || activityTypeCopy[nextStep.step.activity_type].description}
                  </p>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  {(nextStep.status === 'not_started' || nextStep.status === 'in_progress') && (
                    <button
                      onClick={() => setSelectedStep(nextStep)}
                      className="px-4 py-2 rounded-2xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68]"
                    >
                      View
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {tasks.length > 0 && (
          <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-2xl bg-[#fff1e1] flex items-center justify-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Action queue</p>
                <p className="text-xs text-slate-500">Manual tasks assigned by ops</p>
              </div>
            </div>
            <div className="space-y-3">
              {tasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-[#f9fbff] px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{task.title}</p>
                    {task.description && <p className="text-xs text-slate-500 line-clamp-1">{task.description}</p>}
                  </div>
                  <button
                    onClick={() => setSelectedTask(task)}
                    className="px-4 py-2 rounded-2xl bg-lumin-primary/10 text-lumin-primary text-xs font-semibold hover:bg-lumin-primary/20"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-6">
          {groupedSteps
            .filter(({ steps: categorySteps }) => categorySteps.length > 0)
            .map(({ category, steps: categorySteps }) => {
            const isCollapsed = collapsedCategories.has(category.id);
            const allCompleted = categorySteps.length > 0 && categorySteps.every((step) => step.status === 'completed');

            return (
              <div key={category.id} className="rounded-3xl border border-slate-100 bg-white p-6 flex flex-col gap-4 shadow-sm">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs uppercase tracking-[0.4em] text-slate-500">{category.name}</p>
                      {allCompleted && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                          Complete
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400">
                      {categorySteps.filter((item) => item.status === 'completed').length} / {categorySteps.length} completed
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded-2xl"
                      style={{ backgroundColor: `${category.color}20` }}
                    ></div>
                    {isCollapsed ? (
                      <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronUpIcon className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>
                {!isCollapsed && (
                  <div className="space-y-3">
                  {categorySteps.map((item) => (
                    <div
                      key={item.id}
                      className={`rounded-2xl border p-4 cursor-pointer transition-all ${
                        item.status === 'in_progress'
                          ? 'border-amber-200 bg-amber-50 hover:border-amber-300'
                          : item.status === 'completed'
                          ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-300'
                          : 'border-slate-100 bg-[#f9fbff] hover:border-slate-200'
                      }`}
                      onClick={() => setSelectedStep(item)}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-900">{item.step.task_name}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className={`px-3 py-1 text-xs rounded-full ${activityAccent[item.step.activity_type]}`}>
                              {activityTypeCopy[item.step.activity_type].label}
                            </span>
                            <span className={`px-3 py-1 text-xs rounded-full ${priorityColors[item.step.priority]}`}>
                              {item.step.priority.toUpperCase()}
                            </span>
                            <span className={`px-3 py-1 text-xs rounded-full ${statusColors[item.status]}`}>
                              {statusLabels[item.status]}
                            </span>
                            {item.status === 'not_started' && !areDependenciesMet(item) && (
                              <span className="px-3 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                                Waiting
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* View button opens modal for all non-completed steps */}
                          {item.status !== 'completed' && (
                            <button
                              onClick={() => setSelectedStep(item)}
                              className="px-4 py-2 rounded-2xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68]"
                            >
                              View
                            </button>
                          )}
                          {item.status === 'completed' && (
                            <button
                              onClick={() => handleStatusUpdate(item.id, 'not_started')}
                              disabled={updatingStepId === item.id}
                              className="px-4 py-2 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                            >
                              Undo
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>

      {/* Task Detail Modal */}
      {selectedStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedStep(null)}>
          <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold text-slate-900">{selectedStep.step.task_name}</h2>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className={`px-3 py-1 text-xs rounded-full ${activityAccent[selectedStep.step.activity_type]}`}>
                      {activityTypeCopy[selectedStep.step.activity_type].label}
                    </span>
                    <span className={`px-3 py-1 text-xs rounded-full ${priorityColors[selectedStep.step.priority]}`}>
                      {selectedStep.step.priority.toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 text-xs rounded-full ${statusColors[selectedStep.status]}`}>
                      {statusLabels[selectedStep.status]}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedStep(null)}
                  className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedStep.step.notes ? (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Instructions</h3>
                  <div className="space-y-4">
                    {replaceTemplateVars(selectedStep.step.notes, loanOfficer?.location_id).split('\n').map((line, idx) => {
                      // Check if line contains markdown link [text](url)
                      const markdownLinkMatch = line.match(/\[([^\]]+)\]\(([^)]+)\)/);
                      if (markdownLinkMatch) {
                        const [, text, url] = markdownLinkMatch;
                        const beforeLink = line.substring(0, line.indexOf('['));
                        return (
                          <div key={idx} className="flex flex-col gap-2">
                            {beforeLink && <p className="text-slate-700">{beforeLink.trim()}</p>}
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-4 py-2 rounded-2xl bg-lumin-primary text-white font-semibold text-sm hover:bg-[#014c68] transition-colors w-fit"
                            >
                              {text}
                            </a>
                          </div>
                        );
                      }
                      // Check if line contains plain URL
                      const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
                      if (urlMatch) {
                        const parts = line.split(/(https?:\/\/[^\s]+)/);
                        return (
                          <p key={idx} className="text-slate-700">
                            {parts.map((part, i) => {
                              if (part.match(/^https?:\/\//)) {
                                return (
                                  <a
                                    key={i}
                                    href={part}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-lumin-primary hover:text-[#014c68] underline"
                                  >
                                    {part}
                                  </a>
                                );
                              }
                              return part;
                            })}
                          </p>
                        );
                      }
                      // Regular text line
                      return line.trim() ? (
                        <p key={idx} className="text-slate-700">{line}</p>
                      ) : (
                        <div key={idx} className="h-2" />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No additional instructions provided.</p>
              )}

              {/* Dynamic Form Rendering based on input_config */}
              {selectedStep.step.input_config && selectedStep.status !== 'completed' && (
                <>
                  {/* Show resource download if configured */}
                  {selectedStep.step.input_config.showResource && (
                    <ResourceDownload
                      resourceType={selectedStep.step.input_config.showResource}
                      resourceUrl={getResourceUrl(selectedStep.step.input_config.showResource)}
                    />
                  )}

                  {/* Phone Input Form */}
                  {selectedStep.step.input_config.type === 'phone_input' && (
                    <PhoneInputForm
                      config={selectedStep.step.input_config}
                      onSubmit={handlePhoneSubmit}
                      loading={formSubmitting}
                    />
                  )}

                  {/* File Upload Forms (W9, Banking, etc.) */}
                  {(selectedStep.step.input_config.type === 'w9' ||
                    selectedStep.step.input_config.type === 'banking' ||
                    selectedStep.step.input_config.type === 'file_upload') && (
                    <FileUploadForm
                      config={selectedStep.step.input_config}
                      onSubmit={handleFileUpload}
                      loading={formSubmitting}
                    />
                  )}
                </>
              )}

              {/* Show completed message for resource download tasks */}
              {selectedStep.step.input_config?.showResource && selectedStep.status === 'completed' && (
                <ResourceDownload
                  resourceType={selectedStep.step.input_config.showResource}
                  resourceUrl={getResourceUrl(selectedStep.step.input_config.showResource)}
                />
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex flex-col gap-3">
              {/* Show dependency warning if not met */}
              {selectedStep.status === 'not_started' && !areDependenciesMet(selectedStep) && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Waiting on other tasks</p>
                    <p className="text-xs mt-1">
                      Complete these first: {getIncompleteDependencies(selectedStep).join(', ')}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex gap-3 justify-end">
              {/* Show Mark as Complete button for not_started (if deps met) or in_progress (always), without input forms */}
              {((selectedStep.status === 'not_started' && areDependenciesMet(selectedStep)) ||
                selectedStep.status === 'in_progress') &&
               !hasInputForm(selectedStep.step.input_config) && (
                <button
                  onClick={() => {
                    handleStatusUpdate(selectedStep.id, 'completed');
                    setSelectedStep(null);
                  }}
                  disabled={updatingStepId === selectedStep.id}
                  className="px-6 py-3 rounded-2xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68]"
                >
                  Mark as Complete
                </button>
              )}
              {selectedStep.status === 'completed' && (
                <button
                  onClick={() => {
                    handleStatusUpdate(selectedStep.id, 'not_started');
                    setSelectedStep(null);
                  }}
                  disabled={updatingStepId === selectedStep.id}
                  className="px-6 py-3 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  Reset Step
                </button>
              )}
              <button
                onClick={() => setSelectedStep(null)}
                className="px-6 py-3 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Task Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedTask(null)}>
          <div className="bg-white rounded-3xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-semibold text-slate-900">{selectedTask.title}</h2>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-3 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                      Action Item
                    </span>
                    <span className={`px-3 py-1 text-xs rounded-full ${priorityColors[selectedTask.priority]}`}>
                      {selectedTask.priority.toUpperCase()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTask(null)}
                  className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              {selectedTask.description ? (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Details</h3>
                  <div className="space-y-4">
                    {selectedTask.description.split('\n').map((line, idx) => (
                      line.trim() ? (
                        <p key={idx} className="text-slate-700">{line}</p>
                      ) : (
                        <div key={idx} className="h-2" />
                      )
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No additional details provided.</p>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3 justify-end">
              <button
                onClick={() => markTaskComplete(selectedTask.id)}
                className="px-6 py-3 rounded-2xl text-sm font-semibold bg-lumin-primary text-white hover:bg-[#014c68]"
              >
                Mark as Complete
              </button>
              <button
                onClick={() => setSelectedTask(null)}
                className="px-6 py-3 rounded-2xl text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default LoanOfficerWorkspace;
